import { ref, computed } from 'vue'
import { ApolloError } from '@apollo/client/core'

export interface ErrorState {
  hasError: boolean
  errorMessage: string
  errorType: 'network' | 'graphql' | 'unknown'
  retryCount: number
}

/**
 * Composable for handling GraphQL and network errors gracefully
 */
export function useErrorHandler() {
  const errorState = ref<ErrorState>({
    hasError: false,
    errorMessage: '',
    errorType: 'unknown',
    retryCount: 0
  })

  const isNetworkError = computed(() => errorState.value.errorType === 'network')
  const isGraphQLError = computed(() => errorState.value.errorType === 'graphql')
  const canRetry = computed(() => errorState.value.retryCount < 3)

  /**
   * Handle Apollo/GraphQL errors
   */
  function handleError(error: ApolloError | Error | unknown, context = 'Unknown operation') {
    console.error(`Error in ${context}:`, error)

    if (error instanceof ApolloError) {
      // Network errors
      if (error.networkError) {
        errorState.value = {
          hasError: true,
          errorMessage: getNetworkErrorMessage(error.networkError),
          errorType: 'network',
          retryCount: errorState.value.retryCount
        }
        return
      }

      // GraphQL errors
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorState.value = {
          hasError: true,
          errorMessage: getGraphQLErrorMessage([...error.graphQLErrors]),
          errorType: 'graphql',
          retryCount: errorState.value.retryCount
        }
        return
      }
    }

    // Generic error fallback
    errorState.value = {
      hasError: true,
      errorMessage: error instanceof Error ? error.message : 'An unexpected error occurred',
      errorType: 'unknown',
      retryCount: errorState.value.retryCount
    }
  }

  /**
   * Generate user-friendly network error messages
   */
  function getNetworkErrorMessage(networkError: any): string {
    if (networkError.statusCode) {
      switch (networkError.statusCode) {
        case 404:
          return 'Content not found. The requested resource may have been moved or deleted.'
        case 500:
          return 'Server error. Please try again later.'
        case 503:
          return 'Service temporarily unavailable. Please try again in a few moments.'
        default:
          return 'Network connection error. Please check your internet connection and try again.'
      }
    }

    if (networkError.message) {
      if (networkError.message.includes('fetch')) {
        return 'Unable to connect to the server. Please check your internet connection.'
      }
      if (networkError.message.includes('timeout')) {
        return 'Request timed out. Please try again.'
      }
    }

    return 'Network error occurred. Please try again.'
  }

  /**
   * Generate user-friendly GraphQL error messages
   */
  function getGraphQLErrorMessage(graphQLErrors: any[]): string {
    const firstError = graphQLErrors[0]
    
    if (firstError.extensions?.code) {
      switch (firstError.extensions.code) {
        case 'FORBIDDEN':
          return 'Access denied. You may not have permission to view this content.'
        case 'UNAUTHENTICATED':
          return 'Authentication required. Please refresh the page and try again.'
        case 'BAD_USER_INPUT':
          return 'Invalid request. Please refresh the page and try again.'
        default:
          return firstError.message || 'A data error occurred.'
      }
    }

    return firstError.message || 'Unable to load content. Please try again.'
  }

  /**
   * Clear error state
   */
  function clearError() {
    errorState.value = {
      hasError: false,
      errorMessage: '',
      errorType: 'unknown',
      retryCount: 0
    }
  }

  /**
   * Increment retry count
   */
  function incrementRetry() {
    errorState.value.retryCount++
  }

  /**
   * Check if data is empty/null and handle gracefully
   */
  function handleEmptyData(data: any, context = 'data') {
    if (!data) {
      console.warn(`Empty ${context} received`)
      return true
    }
    return false
  }

  /**
   * Safe data access with fallback
   */
  function safeAccess<T>(data: any, path: string, fallback: T): T {
    try {
      const keys = path.split('.')
      let result = data
      
      for (const key of keys) {
        if (result === null || result === undefined) {
          return fallback
        }
        result = result[key]
      }
      
      return result !== undefined ? result : fallback
    } catch (error) {
      console.warn(`Safe access failed for path: ${path}`, error)
      return fallback
    }
  }

  /**
   * Validate GraphQL response structure
   */
  function validateResponse(data: any, requiredPath: string): boolean {
    try {
      return safeAccess(data, requiredPath, null) !== null
    } catch {
      return false
    }
  }

  return {
    errorState: computed(() => errorState.value),
    isNetworkError,
    isGraphQLError,
    canRetry,
    handleError,
    clearError,
    incrementRetry,
    handleEmptyData,
    safeAccess,
    validateResponse
  }
}

/**
 * Global error handler for unhandled promise rejections
 * This version is non-intrusive and only logs errors
 */
export function setupGlobalErrorHandler() {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.warn('Unhandled promise rejection (non-critical):', event.reason)
    
    // Only prevent default for non-critical errors
    if (event.reason && event.reason.message && 
        (event.reason.message.includes('image') || 
         event.reason.message.includes('fetch') ||
         event.reason.message.includes('network'))) {
      event.preventDefault()
    }
  })

  // Handle general JavaScript errors
  window.addEventListener('error', (event) => {
    console.warn('JavaScript error (logged):', event.error)
    
    // Don't interfere with the app's normal operation
    // Only prevent default for known non-critical errors
    if (event.error && event.error.message &&
        (event.error.message.includes('image') ||
         event.error.message.includes('loading'))) {
      event.preventDefault()
    }
  })
}
